import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
  type Theme,
} from '@mui/material';
import type { ConsolidatedEventCluster, SessionEvent } from '@/types';
import { formatVideoTime } from '@/types';
import type { DecisionEventEditValues } from '@/components/review/DecisionEventEditPopover';
import ConsensusComparisonTable from '@/components/review/ConsensusComparisonTable';
import DiscrepancyEventCard from '@/components/review/DiscrepancyEventCard';
import VideoTimestampPreviewDialog from '@/components/review/VideoTimestampPreviewDialog';
import {
  buildConsensusDashboard,
  formatClusterOnFieldDecision,
  getParticipantDiscrepancyClusters,
  getParticipantOfficialAlignment,
  SIGNIFICANT_DISCREPANCY_MAX_AGREEMENT_RATE,
} from '@/components/review/consensus';
import { panelScrollbarSx } from '@/utils/themeSurfaces';

interface Props {
  clusters: ConsolidatedEventCluster[];
  events?: SessionEvent[];
  canEditEvents?: boolean;
  loading?: boolean;
  onJumpToTime?: (seconds: number) => void;
  onEditEvent?: (eventId: string, values: DecisionEventEditValues) => void | Promise<void>;
  participantView?: boolean;
  currentUserId?: number;
  officialUserIds?: number[];
  resolveOfficialLabel?: (userId: number) => string;
  videoDuration?: number;
  videoId?: string | null;
  onPreviewTime?: (seconds: number) => void;
}

/** Shared shell so hero, two-column row, and comparison table align edge-to-edge. */
const analyticsCardSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  bgcolor: 'background.paper',
  width: '100%',
} as const;

const analyticsCardContentSx = {
  p: { xs: 2, sm: 2.5 },
  '&:last-child': { pb: { xs: 2, sm: 2.5 } },
} as const;

const analyticsTwoColumnSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
  gap: 2,
  width: '100%',
  alignItems: 'stretch',
} as const;

/** Fixed height for side-by-side analytics panels on large screens. */
const ANALYTICS_PANEL_HEIGHT = { xs: 'auto', lg: 520 };

function analyticsPanelScrollSx(theme: Theme) {
  return {
    flex: 1,
    minHeight: { xs: 240, lg: 0 },
    maxHeight: { xs: 360, lg: 'none' },
    overflowY: 'auto',
    overflowX: 'hidden',
    pr: 0.5,
    WebkitOverflowScrolling: 'touch',
    ...panelScrollbarSx(theme),
  } as const;
}

function mutedStatus(rate: number) {
  if (rate >= 80) {
    return { bg: 'rgba(46,125,50,0.10)', color: '#2e7d32', border: 'rgba(46,125,50,0.25)' };
  }
  if (rate >= 60) {
    return { bg: 'rgba(96,125,139,0.10)', color: '#546e7a', border: 'rgba(96,125,139,0.25)' };
  }
  return { bg: 'rgba(198,40,40,0.10)', color: '#c62828', border: 'rgba(198,40,40,0.25)' };
}

function AnalyticsMetricsHero({
  metrics,
  subtitle,
}: {
  metrics: Array<{ label: string; value: string; accent?: string }>;
  subtitle?: string;
}) {
  return (
    <Card
      sx={{
        ...analyticsCardSx,
        overflow: 'hidden',
        background: (t) =>
          `linear-gradient(135deg, ${alpha(t.palette.secondary.main, 0.16)} 0%, ${t.palette.background.paper} 42%, ${alpha(t.palette.primary.main, 0.06)} 100%)`,
      }}
    >
      <CardContent sx={analyticsCardContentSx}>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: metrics.length >= 5 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
              lg: metrics.length >= 5 ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
            },
            gap: 2,
            width: '100%',
          }}
        >
          {metrics.map((metric) => (
            <Box
              key={metric.label}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.background.paper, 0.72),
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  lineHeight: 1.1,
                  color: metric.accent || 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2.125rem' },
                }}
              >
                {metric.value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>
                {metric.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function ReviewAnalyticsPanel({
  clusters,
  events = [],
  canEditEvents = false,
  loading,
  onJumpToTime,
  onEditEvent,
  participantView = false,
  currentUserId,
  officialUserIds,
  resolveOfficialLabel,
  videoDuration,
  videoId,
  onPreviewTime,
}: Props) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const dashboard = useMemo(
    () => (clusters.length ? buildConsensusDashboard(clusters, officialUserIds) : null),
    [clusters, officialUserIds]
  );
  const participantStats = useMemo(
    () =>
      participantView && currentUserId != null && clusters.length
        ? getParticipantOfficialAlignment(clusters, currentUserId, officialUserIds)
        : null,
    [clusters, participantView, currentUserId, officialUserIds]
  );
  const [videoPreview, setVideoPreview] = useState<{
    seconds: number;
    subtitle: string;
  } | null>(null);

  const openVideoPreview = useCallback(
    (cluster: ConsolidatedEventCluster) => {
      const seconds = Math.max(0, cluster.anchorTimestamp - 0.8);
      const onField = formatClusterOnFieldDecision(cluster);
      if (videoId) {
        setVideoPreview({
          seconds,
          subtitle: `${formatVideoTime(cluster.anchorTimestamp)} · ${onField}`,
        });
        return;
      }
      onJumpToTime?.(seconds);
    },
    [videoId, onJumpToTime]
  );

  const participantDiscrepancies = useMemo(
    () =>
      participantView && currentUserId != null && clusters.length
        ? getParticipantDiscrepancyClusters(clusters, currentUserId, officialUserIds)
        : [],
    [clusters, participantView, currentUserId, officialUserIds]
  );

  const topMetrics = useMemo(() => {
    if (!dashboard) {
      return [];
    }
    if (participantView && participantStats) {
      const alignColor =
        participantStats.myMarked > 0 && participantStats.alignmentRate >= 80
          ? '#2e7d32'
          : participantStats.myMarked > 0
            ? '#c62828'
            : undefined;
      return [
        { label: '官方事件總數', value: String(participantStats.officialEvents) },
        { label: '已標記事件', value: String(participantStats.myMarked) },
        {
          label: '與官方一致',
          value: `${participantStats.aligned}/${participantStats.myMarked || '—'}`,
        },
        {
          label: '一致率',
          value: participantStats.myMarked > 0 ? `${Math.round(participantStats.alignmentRate)}%` : '—',
          accent: alignColor,
        },
      ];
    }
    const agreementColor =
      dashboard.systemAgreementRate >= 80
        ? '#2e7d32'
        : dashboard.systemAgreementRate >= 60
          ? '#546e7a'
          : '#c62828';
    const discrepancyCount = dashboard.discrepancyClusters.length;
    return [
      { label: '共識事件總數', value: String(dashboard.totalClusters) },
      { label: '參與者總數', value: String(dashboard.totalParticipants) },
      { label: '平均時間偏差 Δt', value: `${dashboard.averageVariance.toFixed(2)} 秒` },
      { label: '全局共識率', value: `${Math.round(dashboard.systemAgreementRate)}%`, accent: agreementColor },
      {
        label: '顯著爭議事件',
        value: String(discrepancyCount),
        accent: discrepancyCount > 0 ? '#c62828' : undefined,
      },
    ];
  }, [participantView, participantStats, dashboard]);

  if (loading) return <LinearProgress />;

  if (!clusters.length || !dashboard) {
    return (
      <Typography color="text.secondary">
        {participantView ? '尚無可比對的官方判決事件' : '尚無可分析的共識事件'}
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <VideoTimestampPreviewDialog
        open={!!videoPreview}
        onClose={() => setVideoPreview(null)}
        videoId={videoId}
        initialSeconds={videoPreview?.seconds ?? 0}
        videoDuration={videoDuration}
        subtitle={videoPreview?.subtitle}
      />
      <AnalyticsMetricsHero
        metrics={topMetrics}
        subtitle={
          participantView
            ? '與官方判決比對的個人統計摘要'
            : '本群組分享場次之整合分析摘要'
        }
      />

      {!participantView && (
        <Box sx={analyticsTwoColumnSx}>
            <Card
              sx={{
                ...analyticsCardSx,
                display: 'flex',
                flexDirection: 'column',
                minHeight: ANALYTICS_PANEL_HEIGHT,
                maxHeight: ANALYTICS_PANEL_HEIGHT,
                minWidth: 0,
              }}
            >
              <CardContent
                sx={{
                  ...analyticsCardContentSx,
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ flexShrink: 0 }}>
                  參與者一致性排行榜
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, flexShrink: 0 }}>
                  一致率為覆核判決評價、覆核球權、覆核罰則三項與官方吻合的平均（僅計官方亦有標記的事件）
                </Typography>
                {isMobileLayout ? (
                  <Stack spacing={0.75} sx={analyticsPanelScrollSx(theme)}>
                    {dashboard.leaderboardRows.map((row) => {
                      const tone = mutedStatus(row.alignmentScore);
                      return (
                        <Box
                          key={row.userId}
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0, pr: 1 }}>
                              {row.userName}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                row.officialEventMarks > 0
                                  ? `${Math.round(row.alignmentScore)}%`
                                  : '—'
                              }
                              sx={{
                                height: 22,
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                flexShrink: 0,
                                bgcolor: tone.bg,
                                color: tone.color,
                                border: '1px solid',
                                borderColor: tone.border,
                              }}
                            />
                          </Stack>
                          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
                            <Typography variant="caption" color="text.secondary">
                              總標記 {row.totalMarks}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              官方事件 {row.officialEventMarks}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              覆核判決 {Math.round(row.judgementRate)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              覆核球權 {Math.round(row.reviewedBallRate)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              覆核罰則 {Math.round(row.reviewedSanctionRate)}%
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                <TableContainer sx={analyticsPanelScrollSx(theme)}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{ '& .MuiTableCell-root': { borderColor: 'divider', whiteSpace: 'nowrap' } }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>用戶</TableCell>
                        <TableCell align="right">總標記</TableCell>
                        <TableCell align="right">官方事件</TableCell>
                        <TableCell align="right">一致率</TableCell>
                        <TableCell align="right">覆核判決評價</TableCell>
                        <TableCell align="right">覆核球權</TableCell>
                        <TableCell align="right">覆核罰則</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboard.leaderboardRows.map((row) => {
                        const tone = mutedStatus(row.alignmentScore);
                        return (
                          <TableRow key={row.userId} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {row.userName}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontFamily="monospace">
                                {row.totalMarks}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontFamily="monospace">
                                {row.officialEventMarks}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                size="small"
                                label={
                                  row.officialEventMarks > 0
                                    ? `${Math.round(row.alignmentScore)}%`
                                    : '—'
                                }
                                sx={{
                                  height: 22,
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  bgcolor: tone.bg,
                                  color: tone.color,
                                  border: '1px solid',
                                  borderColor: tone.border,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color="text.secondary">
                                {row.officialEventMarks > 0 ? `${Math.round(row.judgementRate)}%` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color="text.secondary">
                                {row.officialEventMarks > 0 ? `${Math.round(row.reviewedBallRate)}%` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color="text.secondary">
                                {row.officialEventMarks > 0 ? `${Math.round(row.reviewedSanctionRate)}%` : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
              </CardContent>
            </Card>
            <Card
              sx={{
                ...analyticsCardSx,
                display: 'flex',
                flexDirection: 'column',
                minHeight: ANALYTICS_PANEL_HEIGHT,
                maxHeight: ANALYTICS_PANEL_HEIGHT,
                minWidth: 0,
              }}
            >
              <CardContent
                sx={{
                  ...analyticsCardContentSx,
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ flexShrink: 0 }}>
                  顯著爭議事件清單
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, flexShrink: 0 }}>
                  僅顯示共識% ≤ {SIGNIFICANT_DISCREPANCY_MAX_AGREEMENT_RATE}% 的事件
                </Typography>
                {dashboard.discrepancyClusters.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    所有事件共識% 均高於 {SIGNIFICANT_DISCREPANCY_MAX_AGREEMENT_RATE}%
                  </Typography>
                ) : (
                  <Box sx={analyticsPanelScrollSx(theme)}>
                    <Stack spacing={0.75} sx={{ pb: 0.5 }}>
                      {dashboard.discrepancyClusters.map((cluster) => (
                        <DiscrepancyEventCard
                          key={cluster.clusterId}
                          cluster={cluster}
                          officialUserIds={officialUserIds}
                          onPlayVideo={videoId ? openVideoPreview : undefined}
                          onJumpToTime={videoId ? undefined : onJumpToTime}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
        </Box>
      )}

      {participantView && participantDiscrepancies.length > 0 && (
        <Card sx={analyticsCardSx}>
          <CardContent sx={analyticsCardContentSx}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              與官方分歧的事件
            </Typography>
            <Stack spacing={0.75}>
              {participantDiscrepancies.map((cluster) => (
                <DiscrepancyEventCard
                  key={cluster.clusterId}
                  cluster={cluster}
                  officialUserIds={officialUserIds}
                  onPlayVideo={videoId ? openVideoPreview : undefined}
                  onJumpToTime={videoId ? undefined : onJumpToTime}
                  participantView
                  currentUserId={currentUserId}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {participantView && participantStats && participantStats.missedEvents > 0 && (
        <Typography variant="body2" color="text.secondary">
          尚有 {participantStats.missedEvents} 個官方事件您尚未標記，可在下方判決比對表查看詳情。
        </Typography>
      )}

      <Card sx={analyticsCardSx}>
        <CardContent sx={analyticsCardContentSx}>
          <ConsensusComparisonTable
            clusters={clusters}
            events={events}
            canEditEvents={canEditEvents}
            onEditEvent={onEditEvent}
            onJumpToTime={onJumpToTime}
            participantView={participantView}
            currentUserId={currentUserId}
            officialUserIds={officialUserIds}
            resolveOfficialLabel={resolveOfficialLabel}
            videoDuration={videoDuration}
            videoId={videoId}
            onPreviewTime={onPreviewTime}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}
